import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";  // PROMPT 144
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft,
  Archive,
  ArchiveRestore,
  Bell,
  Smartphone,
  EyeOff,
  Pin,
  PinOff,
  BookOpen,
  Bot,
  ChevronDown,
  Clock,
  FileText,
  CreditCard,
  GraduationCap,
  Loader2,
  MessageCircle,
  MoreHorizontal,
  Ban,
  Megaphone,
  Radio,
  RefreshCw,
  Search,
  User,
  UserCheck,
} from "lucide-react";
import ColaPagosDialog from "@/components/agente/ColaPagosDialog";
// PROMPT 123: el chat con fotos, audios, documentos y notas de voz, como WhatsApp.
import ChatWhatsApp, { Avatar, aFecha, horaLista } from "@/components/agente/ChatWhatsApp";
import CanalWhatsAppDialog from "@/components/agente/CanalWhatsAppDialog";
// PROMPT 146: plantillas de WhatsApp desde la plataforma.
import PlantillasDialog from "@/components/agente/PlantillasDialog";
import DifusionesDialog from "@/components/agente/DifusionesDialog"; // PROMPT 151
import NuevoChatDialog from "@/components/agente/NuevoChatDialog";
import { ElegirPlantillaDialog } from "@/components/agente/ElegirPlantilla";
import ConocimientoDialog from "@/components/agente/ConocimientoDialog";
// PROMPT 145: avisos en vivo, no leidos y avisos de este navegador (AvisosWhatsApp.jsx).
import { useAuth } from "@/context/AuthContext";
import { AvisosEscritorioDialog, useAvisosWa } from "@/components/agente/AvisosWhatsApp";
// PROMPT 152: avisos del telefono (push) en este dispositivo.
import AvisosPushDialog from "@/components/agente/AvisosPushDialog";
// PROMPT 144: el color de cada marca y como se ve el cliente.
import { SIN_NUMERO, colorDeMarca, nombreCorto, nombreParaAvatar, nombreVisible, tonos } from "@/lib/marcasWa";
// PROMPT 149: respuestas rapidas, etiquetas, notas y ficha del cliente.
import { Zap as IconoRapidas, Tag as IconoEtiqueta, Info as IconoInfo } from "lucide-react";
import { RespuestasRapidasDialog } from "@/components/agente/RespuestasRapidas";
import {
  EtiquetasDeLaConversacion,
  EtiquetasDialog,
  EtiquetasEnFila,
} from "@/components/agente/EtiquetasWhatsApp";
import FichaCliente from "@/components/agente/FichaCliente";
import { ChipBloqueado, useBloqueo } from "@/components/agente/BloqueoCliente";

// PROMPT 88 (2026-09-01): pantalla "Agente WhatsApp".
// Izquierda: conversaciones (del simulador y, mas adelante, de WhatsApp real).
// Derecha: el hilo + un cuadro para escribir COMO EL CLIENTE (simulador). Sirve para
// probar y demostrar el agente con datos reales antes de conectar Meta.

const ESTADOS = {
  agente: { label: "Atiende el agente", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  persona: { label: "Esperando a una persona", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  cerrada: { label: "Cerrada", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

// PROMPT 120: conversacion de WhatsApp de una marca donde el agente NO contesta solo ahora
// (esta en "Nunca", o es horario del equipo). No dice "Atiende el agente" porque no la
// contesta nadie solo: la tiene que contestar alguien, desde aca o desde el celular.
const SIN_AGENTE = { label: "Sin respuesta automática", cls: "bg-orange-50 text-orange-700 border-orange-200" };

// PROMPT 144: en la lista el estado va con una etiqueta corta, para que entre con la marca y el telefono.
const ETIQUETAS_CORTAS = {
  "Atiende el agente": "Agente",
  "Esperando a una persona": "Persona",
  "La atiende el equipo (celular)": "Equipo (celular)",
  "Sin respuesta automática": "Sin agente",
};

// PROMPT 145: la lista llega de a paginas y la filtra el servidor.
const LIMITE_LISTA = 50;
const LIMITE_LISTA_MAX = 200;

// Selector "Mostrar". Los que son "filtro" los resuelve el servidor (por persona y por hora).
const MOSTRAR = [
  { valor: "todas", etiqueta: "Todas" },
  { valor: "sin_leer", etiqueta: "Sin leer" },
  { valor: "mias", etiqueta: "Mías" },
  { valor: "sin_asignar", etiqueta: "Sin asignar" },
  { valor: "persona", etiqueta: "Esperando a una persona" },
  { valor: "agente", etiqueta: "Atiende el agente" },
  { valor: "ventana_abierta", etiqueta: "Ventana abierta" },
  { valor: "cerrada", etiqueta: "Archivadas" },
];
const MOSTRAR_COMO_FILTRO = new Set(["sin_leer", "mias", "sin_asignar", "ventana_abierta"]);

function paramsDeMostrar(mostrar) {
  if (MOSTRAR_COMO_FILTRO.has(mostrar)) return { filtro: mostrar };
  if (mostrar && mostrar !== "todas") return { estado: mostrar };
  return {};
}

// Si una conversacion que llega en vivo entra en la lista de ahora. null = no se puede saber sin
// preguntarle al servidor ("ventana abierta" depende de la hora del ultimo mensaje del cliente).
function coincideConLista(c, mostrar, marcaSlug, usuarioId, etiqueta) {
  if (marcaSlug && c.marca_slug !== marcaSlug) return false;
  if (etiqueta && !(c.etiquetas || []).includes(etiqueta)) return false; // PROMPT 149
  if (mostrar === "cerrada") return c.estado === "cerrada";
  if (c.estado === "cerrada") return false;
  if (mostrar === "agente" || mostrar === "persona") return c.estado === mostrar;
  if (mostrar === "sin_leer") return (c.no_leidos || 0) > 0;
  if (mostrar === "mias") return c.asignada_a === usuarioId;
  if (mostrar === "sin_asignar") return !c.asignada_a;
  if (mostrar === "ventana_abierta") return null;
  return true;
}

// Las fijadas arriba (la ultima fijada primero) y el resto por la hora del ultimo movimiento.
function ordenarLista(lista) {
  const hora = (valor) => {
    const f = aFecha(valor);
    return f ? f.getTime() : 0;
  };
  const fijadas = lista.filter((c) => c.fijada).sort((a, b) => hora(b.fijada_en) - hora(a.fijada_en));
  const resto = lista.filter((c) => !c.fijada).sort((a, b) => hora(b.updated_at) - hora(a.updated_at));
  return [...fijadas, ...resto];
}

// Iniciales de la persona a la que esta asignada (se ven en la lista).
function iniciales(nombre) {
  const partes = String(nombre || "").trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return "?";
  return (partes[0][0] + (partes.length > 1 ? partes[partes.length - 1][0] : "")).toUpperCase();
}

// Abrir una conversacion la deja leida (POST /leer); un error no frena nada.
function marcarLeida(id) {
  api.post(`/whatsapp/conversaciones/${id}/leer`).catch(() => {});
}
function EstadoBadge({ estado, celular, sinAgente, corto }) {
  const clave = ESTADOS[estado] ? estado : "agente";
  const e = clave === "agente" && sinAgente ? SIN_AGENTE : ESTADOS[clave];
  // PROMPT 119: la contestó alguien del equipo desde la app del celular (coexistencia).
  const largo = estado === "persona" && celular ? "La atiende el equipo (celular)" : e.label;
  const label = corto ? ETIQUETAS_CORTAS[largo] || largo : largo;
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium border ${e.cls}`}
      title={e === SIN_AGENTE ? "El agente no le contesta solo a esta conversación en este momento (por el modo de la marca, el horario o porque el número está en \"No contestar nunca\"). Se cambia en Canal." : undefined}
      data-testid={e === SIN_AGENTE ? "wa-sin-agente" : undefined}
    >
      {label}
    </span>
  );
}

// PROMPT 123: las horas (en la hora de la computadora) estan en components/agente/ChatWhatsApp.jsx.

// PROMPT 123: la burbuja de cada mensaje (con fotos, audios, documentos y los tildes de
// enviado / entregado / leido) esta en components/agente/ChatWhatsApp.jsx.

// PROMPT 143: hay clientes que Meta identifica solo por su nombre de usuario (sin teléfono visible).
// PROMPT 144: como se ve el cliente (nombreVisible) y el color de cada marca estan en lib/marcasWa.js.

// PROMPT 143: el detalle trae solo los ÚLTIMOS mensajes. Si la conversación abierta es la misma, se
// mezcla por id para no tirar lo que el equipo ya cargó de más con "Cargar mensajes anteriores".
function mezclarDetalle(actual, data) {
  if (!actual || actual.id !== data?.id) return data;
  const nuevos = data.mensajes || [];
  const ids = new Set(nuevos.map((m) => m.id));
  const primera = nuevos.length ? aFecha(nuevos[0].created_at) : null;
  if (primera === null) return data;
  const previos = (actual.mensajes || []).filter((m) => {
    const f = aFecha(m.created_at);
    return !ids.has(m.id) && f !== null && f.getTime() < primera.getTime();
  });
  if (!previos.length) return data;
  return { ...data, mensajes: [...previos, ...nuevos], hay_anteriores: actual.hay_anteriores };
}

// PROMPT 144: la pantalla se usa como WhatsApp Web. En el celular el chat abierto ocupa toda la
// pantalla y tiene que seguir la altura VISIBLE (con el teclado abierto el cuadro de escribir
// queda a la vista); desde 1024 px de ancho hay dos paneles.
function leerViewport() {
  const vv = window.visualViewport;
  return {
    ancha: window.matchMedia ? window.matchMedia("(min-width: 1024px)").matches : window.innerWidth >= 1024,
    alto: Math.round(vv ? vv.height : window.innerHeight),
    arriba: Math.round(vv ? vv.offsetTop : 0),
  };
}

function useViewport() {
  const [vista, setVista] = useState(leerViewport);
  useEffect(() => {
    const actualizar = () =>
      setVista((anterior) => {
        const ahora = leerViewport();
        return ahora.ancha === anterior.ancha && ahora.alto === anterior.alto && ahora.arriba === anterior.arriba
          ? anterior
          : ahora;
      });
    const vv = window.visualViewport;
    window.addEventListener("resize", actualizar);
    if (vv) {
      vv.addEventListener("resize", actualizar);
      vv.addEventListener("scroll", actualizar);
    }
    return () => {
      window.removeEventListener("resize", actualizar);
      if (vv) {
        vv.removeEventListener("resize", actualizar);
        vv.removeEventListener("scroll", actualizar);
      }
    };
  }, []);
  return vista;
}

// PROMPT 144: menu chico, sin librerias: se cierra al tocar afuera, con Escape o al elegir algo
// (lo que tiene `data-cerrar`). Lo que no lo tiene (el selector de turno) deja el menu abierto.
function MenuChico({ boton, testid, titulo, ancho = "w-64", children }) {
  const [abierto, setAbierto] = useState(false);
  const caja = useRef(null);
  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => {
      if (caja.current && !caja.current.contains(e.target)) setAbierto(false);
    };
    const tecla = (e) => {
      if (e.key === "Escape") setAbierto(false);
    };
    document.addEventListener("pointerdown", fuera);
    document.addEventListener("keydown", tecla);
    return () => {
      document.removeEventListener("pointerdown", fuera);
      document.removeEventListener("keydown", tecla);
    };
  }, [abierto]);
  return (
    <div className="relative" ref={caja}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setAbierto((v) => !v)}
        title={titulo}
        aria-haspopup="menu"
        aria-expanded={abierto}
        data-testid={testid}
        className="gap-1.5"
      >
        {boton}
      </Button>
      {abierto && (
        <div
          role="menu"
          className={`absolute right-0 z-50 mt-1 ${ancho} rounded-md border border-slate-200 bg-white p-1 shadow-lg`}
          onClick={(e) => {
            if (e.target.closest("[data-cerrar]")) setAbierto(false);
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

function ItemMenu({ icono: Icono, onClick, testid, children }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      data-cerrar="true"
      data-testid={testid}
      className="flex w-full items-center gap-2 rounded px-2.5 py-2 text-left text-sm text-slate-800 hover:bg-slate-100"
    >
      {Icono ? <Icono className="h-4 w-4 shrink-0 text-slate-500" /> : null}
      <span className="flex min-w-0 flex-1 items-center gap-1.5">{children}</span>
    </button>
  );
}

const Contador = ({ n }) => (
  <span className="rounded bg-amber-100 px-1.5 text-[11px] text-amber-800">{n}</span>
);

export default function AgenteWhatsAppPage() {
  const [convs, setConvs] = useState([]);
  // PROMPT 144: la direccion manda lo que se ve: /agente?c=<id> abre ese chat (los avisos de
  // mensajes nuevos usan esa direccion) y /agente?prueba=1 la conversacion de prueba nueva. En el
  // celular, la flecha y el "atras" del navegador vuelven a la lista.
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const idUrl = params.get("c") || "";
  const enPrueba = params.get("prueba") === "1";
  const [marcaFiltro, setMarcaFiltro] = useState("");
  const vista = useViewport();
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState("todas");
  // PROMPT 149: filtro por etiqueta, etiquetas del equipo y pantallas nuevas.
  const [etiquetaFiltro, setEtiquetaFiltro] = useState("");
  const [etiquetas, setEtiquetas] = useState([]);
  const [rapidasOpen, setRapidasOpen] = useState(false);
  const [etiquetasOpen, setEtiquetasOpen] = useState(false);
  const [fichaOpen, setFichaOpen] = useState(false);
  // PROMPT 145: avisos en vivo, lista paginada y quien esta escribiendo.
  const { user, isAdmin } = useAuth();
  const avisos = useAvisosWa();
  const usuarioId = user?.id || null;
  const [siguiente, setSiguiente] = useState(null); // X-Siguiente: desde donde sigue la lista
  const [totalLista, setTotalLista] = useState(null); // X-Total-Count
  const [cargandoMas, setCargandoMas] = useState(false);
  const [escribiendo, setEscribiendo] = useState(null); // otra persona del equipo escribiendo
  const [avisosOpen, setAvisosOpen] = useState(false);
  const [avisosPushOpen, setAvisosPushOpen] = useState(false); // PROMPT 152
  const convsRef = useRef([]);
  convsRef.current = convs;
  const mostrarRef = useRef({});
  mostrarRef.current = { estadoFiltro, marcaFiltro, etiquetaFiltro, q, usuarioId };
  const contenedorLista = useRef(null);
  const centinela = useRef(null);
  const ultimoEscribiendo = useRef(0);
  const ultimoErrorMas = useRef(0);
  const temporizadores = useRef({});
  const [sel, setSel] = useState(null); // conversacion completa (con mensajes)
  const [telefono, setTelefono] = useState("+5730212345678");
  const [nombre, setNombre] = useState("");
  // PROMPT 90 (2026-09-04): marca por la que entra la conversacion simulada.
  const [marcas, setMarcas] = useState([]);
  const [marcaSlug, setMarcaSlug] = useState("");
  const [texto, setTexto] = useState("");
  const [cita, setCita] = useState(null); // PROMPT 148: mensaje al que se está respondiendo
  const [enviando, setEnviando] = useState(false);
  const [estadoAgente, setEstadoAgente] = useState(null);
  const [pagosOpen, setPagosOpen] = useState(false); // PROMPT 91: cola de pagos
  const [canalOpen, setCanalOpen] = useState(false); // PROMPT 94: canal real de WhatsApp
  const [plantillasOpen, setPlantillasOpen] = useState(false); // PROMPT 146: plantillas de WhatsApp
  const [difusionesOpen, setDifusionesOpen] = useState(false); // PROMPT 151: difusiones (solo admin)
  const [elegirOpen, setElegirOpen] = useState(false); // PROMPT 147: mandar una plantilla desde el chat
  const [nuevoOpen, setNuevoOpen] = useState(false); // PROMPT 147: empezar un chat nuevo
  const [nuevoInicial, setNuevoInicial] = useState(null); // lo que ya viene escrito (por ejemplo desde una reserva)
  const [conocimientoOpen, setConocimientoOpen] = useState(false); // PROMPT 97: qué sabe
  const [preguntaParaEnsenar, setPreguntaParaEnsenar] = useState(""); // PROMPT 98
  const [pendientes, setPendientes] = useState([]); // lo que no supo contestar
  const [pendientesOpen, setPendientesOpen] = useState(false);
  const [turno, setTurno] = useState(null); // PROMPT 106: quien atiende lo que el agente deriva
  // PROMPT 95: "cliente" = escribir como si fuera el cliente (simulador, para probar).
  // "persona" = contestarle DE VERDAD al cliente desde acá. En las conversaciones que
  // vienen de WhatsApp arranca en "persona", que es lo que el equipo va a querer hacer.
  const [modo, setModo] = useState("cliente");
  const esReal = sel?.canal === "whatsapp";
  const ventanaCerrada = esReal && sel?.ventana_abierta === false;
  // PROMPT 144: en una conversacion de WhatsApp real SIEMPRE se responde como persona del
  // equipo: no hay forma de simular al cliente. Solo en las del simulador se elige el modo.
  const esSimulador = sel?.canal === "simulador";
  const modoEfectivo = sel && !esSimulador ? "persona" : modo;
  // PROMPT 123: el refresco automatico (ver mas abajo).
  const pedidoLista = useRef(0);
  const refrescandoConversacion = useRef(false);
  const selIdRef = useRef(null);
  selIdRef.current = sel?.id || null;

  // PROMPT 123: `{ silencioso: true }` es el refresco automatico: sin la ruedita y sin
  // avisos de error. Si se piden dos listas, vale la ultima que se pidio.
  const cargarLista = useCallback(async (opciones) => {
    const silencioso = opciones?.silencioso === true;
    const pedido = ++pedidoLista.current;
    if (!silencioso) setLoading(true);
    try {
      // PROMPT 145: el servidor filtra (marca, "Mostrar", busqueda) y pagina. La actualizacion
      // automatica pide lo que ya se ve, para no perder las paginas que se fueron cargando.
      const yaCargadas = convsRef.current.filter((c) => !c.fijada).length;
      const params = {
        ...paramsDeMostrar(estadoFiltro),
        limite: silencioso ? Math.min(LIMITE_LISTA_MAX, Math.max(LIMITE_LISTA, yaCargadas)) : LIMITE_LISTA,
      };
      if (marcaFiltro) params.marca_slug = marcaFiltro;
      if (etiquetaFiltro) params.etiqueta = etiquetaFiltro; // PROMPT 149
      if (q) params.q = q;
      const [{ data, headers }, est] = await Promise.all([
        api.get("/whatsapp/conversaciones", { params }),
        api.get("/whatsapp/estado").catch(() => ({ data: null })),
      ]);
      if (pedido !== pedidoLista.current) return;
      setConvs(data || []);
      setSiguiente(headers?.["x-siguiente"] || null);
      const total = Number(headers?.["x-total-count"]);
      setTotalLista(Number.isFinite(total) ? total : null);
      setEstadoAgente(est?.data || null);
    } catch (e) {
      if (!silencioso) toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      if (!silencioso) setLoading(false);
    }
  }, [estadoFiltro, marcaFiltro, etiquetaFiltro, q]);

  // PROMPT 145: al escribir en el buscador se espera un momento (la busqueda mira tambien el texto
  // de los mensajes).
  useEffect(() => {
    const espera = setTimeout(() => cargarLista(), q ? 300 : 0);
    return () => clearTimeout(espera);
  }, [cargarLista, q]);

  useEffect(() => {
    // PROMPT 91: solo las marcas que atiende el agente (las cuatro del cliente), no
    // todas las marcas de la app.
    api.get("/whatsapp/marcas")
      .then(({ data }) => {
        const activas = data || [];
        setMarcas(activas);
        if (!marcaSlug && activas.length) setMarcaSlug(activas[0].slug);
      })
      .catch(() => setMarcas([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nombreMarca = (slug) => marcas.find((m) => m.slug === slug)?.remitente_nombre || marcas.find((m) => m.slug === slug)?.nombre || slug || "";

  // PROMPT 120: conversacion de WhatsApp de una marca donde el agente no contesta solo ahora.
  // Solo las de WhatsApp: en el simulador el agente contesta siempre.
  // PROMPT 127: el servidor lo calcula por conversacion (modo prueba y numeros que no se
  // contestan dependen del numero); si no vino, se usa el dato de la marca como antes.
  const sinAgente = (c) =>
    c?.canal === "whatsapp" &&
    (typeof c?.agente_contesta_ahora === "boolean"
      ? !c.agente_contesta_ahora
      : estadoAgente?.agente_contesta?.[c?.marca_slug] === false);

  // PROMPT 144: la lista se puede filtrar por marca (en pantalla; desde el PROMPT 145 lo hace el servidor).
  const convsVisibles = convs; // PROMPT 145: la marca ya la filtro el servidor (marca_slug)
  // PROMPT 123: como en WhatsApp, lo nuevo aparece solo. La conversacion abierta se refresca
  // cada 5 segundos y la lista cada 15, solo con la pestaña a la vista. El hilo baja solo al
  // ultimo mensaje (ChatWhatsApp).
  // PROMPT 145: lo nuevo llega EN VIVO por el WebSocket (mas abajo). Este refresco queda de
  // respaldo: la conversacion abierta cada 20 segundos y la lista cada 60.
  const selId = sel?.id;
  useEffect(() => {
    if (!selId) return undefined;
    let vigente = true;
    const reloj = setInterval(async () => {
      if (document.visibilityState !== "visible" || refrescandoConversacion.current) return;
      refrescandoConversacion.current = true;
      try {
        const { data } = await api.get(`/whatsapp/conversaciones/${selId}`);
        if (vigente) {
          setSel((actual) => {
            if (actual?.id !== data?.id) return actual;
            const mezcla = mezclarDetalle(actual, data);
            return JSON.stringify(actual) !== JSON.stringify(mezcla) ? mezcla : actual;
          });
        }
      } catch {
        /* sin conexion: se vuelve a intentar en el proximo ciclo */
      } finally {
        refrescandoConversacion.current = false;
      }
    }, 20000);
    return () => {
      vigente = false;
      clearInterval(reloj);
    };
  }, [selId]);
  useEffect(() => {
    const reloj = setInterval(() => {
      if (document.visibilityState === "visible") cargarLista({ silencioso: true });
    }, 60000);
    return () => clearInterval(reloj);
  }, [cargarLista]);

  // PROMPT 144: abrir una conversacion = ir a /agente?c=<id> (queda en el historial: el "atras" del
  // navegador vuelve a la lista). La carga la hace el efecto de mas abajo.
  const abrir = (c) => {
    if (!c?.id) return;
    setParams({ c: c.id }, { state: idUrl || enPrueba ? undefined : { desdeLista: true } });
  };

  const aplicarConversacion = (data) => {
    setSel(data);
    if (data.canal === "whatsapp") marcarLeida(data.id); // PROMPT 145
    setTelefono(data.telefono || telefono);
    setNombre(data.nombre_cliente || "");
    // PROMPT 95: si la conversación es de WhatsApp real, lo que el equipo va a querer es
    // contestarle al cliente, no simular. En las de prueba se sigue simulando.
    setModo(data.canal === "whatsapp" ? "persona" : "cliente");
    setTexto("");
  };

  useEffect(() => {
    if (!idUrl) {
      // sin conversacion en la direccion: se volvio a la lista (flecha o "atras")
      setSel((actual) => (actual ? null : actual));
      return undefined;
    }
    if (selIdRef.current === idUrl) return undefined;
    let vigente = true;
    (async () => {
      try {
        const { data } = await api.get(`/whatsapp/conversaciones/${idUrl}`);
        if (vigente) aplicarConversacion(data);
      } catch (e) {
        if (vigente) {
          toast.error(formatApiError(e.response?.data?.detail));
          setParams({}, { replace: true });
        }
      }
    })();
    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idUrl]);

  // PROMPT 144: la flecha de volver (celular): si se llego desde la lista, es el "atras" del navegador.
  const irALista = () => {
    if (location.state?.desdeLista) navigate(-1);
    else setParams({}, { replace: true });
  };
    // PROMPT 147: /agente?reserva=<id> (el boton de WhatsApp de Reservas): abre el chat de ese pasajero o,
    // si todavia no hay uno, el "Nuevo chat" con los datos de la reserva ya escritos.
    const reservaUrl = params.get("reserva") || "";
    useEffect(() => {
      if (!reservaUrl) return undefined;
      let vigente = true;
      (async () => {
        try {
          const { data } = await api.get(`/whatsapp/para-reserva/${encodeURIComponent(reservaUrl)}`);
          if (!vigente) return;
          if (data?.conversacion_id) {
            setParams({ c: data.conversacion_id }, { replace: true });
            return;
          }
          let aviso = null;
          if (!data?.telefono) aviso = "Esta reserva no tiene teléfono: escribilo para poder mandar la plantilla.";
          else if (!data?.marca_slug) aviso = "La marca de esta reserva no tiene el número de WhatsApp conectado: elegí desde qué marca escribirle.";
          setNuevoInicial({
            reserva_id: reservaUrl,
            marca_slug: data?.marca_slug || null,
            telefono: data?.telefono || "",
            nombre: data?.nombre || "",
            idioma: data?.idioma || undefined,
            valores_sugeridos: data?.valores_sugeridos || {},
            aviso,
          });
          setNuevoOpen(true);
          setParams({}, { replace: true });
        } catch (e) {
          if (vigente) {
            toast.error(formatApiError(e.response?.data?.detail) || "No se pudo abrir WhatsApp para esa reserva.");
            setParams({}, { replace: true });
          }
        }
      })();
      return () => {
        vigente = false;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reservaUrl]);

  // PROMPT 143: trae los mensajes más viejos de la conversación abierta y los agrega arriba.
  const cargarAnteriores = async () => {
    const abierta = sel;
    const primero = abierta?.mensajes?.[0];
    if (!abierta?.id || !primero?.created_at) return;
    try {
      const { data } = await api.get(`/whatsapp/conversaciones/${abierta.id}/mensajes`, {
        params: { antes: primero.created_at, limite: 200 },
      });
      setSel((ahora) => {
        if (ahora?.id !== abierta.id) return ahora;
        const lista = ahora.mensajes || [];
        const ids = new Set(lista.map((m) => m.id));
        const agregados = (data.mensajes || []).filter((m) => !ids.has(m.id));
        return { ...ahora, mensajes: [...agregados, ...lista], hay_anteriores: Boolean(data.hay_anteriores) };
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const nueva = () => {
    setSel(null);
    setModo("cliente");
    setNombre("");
    const sufijo = String(Math.floor(1000 + Math.random() * 9000));
    setTelefono(`+57300000${sufijo}`);
    setParams({ prueba: "1" }, { state: idUrl || enPrueba ? undefined : { desdeLista: true } });  // PROMPT 144
    setTexto("");
  };

  const enviar = async () => {
    const t = texto.trim();
    if (!t) return;
    setEnviando(true);
    try {
      // PROMPT 95: responder DE VERDAD, como persona del equipo.
      if (modoEfectivo === "persona" && sel) {
        const { data } = await api.post(`/whatsapp/conversaciones/${sel.id}/responder`, {
        texto: t,
        ...(cita ? { responder_a: cita.id } : {}), // PROMPT 148
      });
        setTexto("");
        setCita(null);
        const { data: full } = await api.get(`/whatsapp/conversaciones/${sel.id}`);
        setSel(full);
        cargarLista();
        if (data?.envio?.simulado) {
          toast.message("Anotado en el hilo. El canal todavía está en simulación: no salió a WhatsApp.");
        } else if (data?.envio?.ok === false) {
          toast.error(`No se pudo enviar: ${data.envio.error}`);
        } else {
          toast.success("Respuesta enviada al cliente.");
        }
        return;
      }
      const { data } = await api.post("/whatsapp/simulador/mensaje", {
        telefono: sel?.telefono || telefono,
        texto: t,
        nombre_cliente: nombre || null,
        marca_slug: sel?.marca_slug || marcaSlug || null,
      });
      setTexto("");
      const { data: full } = await api.get(`/whatsapp/conversaciones/${data.conversacion.id}`);
      setParams({ c: full.id }, { replace: true });  // PROMPT 144: la conversacion nueva queda en la direccion
      setSel(full);
      cargarLista();
      if (!data.saliente) toast.message("La conversación la sigue una persona: el agente no respondió.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setEnviando(false);
    }
  };

  // PROMPT 123: despues de mandar una foto, un documento o una nota de voz.
  const alEnviarArchivo = async (data) => {
    const id = data?.conversacion?.id;
    if (id && id === selIdRef.current) {
      try {
        const { data: full } = await api.get(`/whatsapp/conversaciones/${id}`);
        if (id === selIdRef.current) setSel(full);
      } catch {
        /* lo trae el refresco automatico */
      }
    }
    cargarLista({ silencioso: true });
    if (data?.envio?.simulado) {
      toast.message("Anotado en el hilo. El canal todavía está en simulación: no salió a WhatsApp.");
    } else if (data?.envio?.ok === false) {
      toast.error(`No se pudo enviar: ${data.envio.error}`);
    }
  };
      // PROMPT 147: despues de mandar (o intentar mandar) una plantilla se recarga el chat abierto: el mensaje
      // queda en el hilo con su estado y, si Meta lo rechazo, con la explicacion.
      const alElegirPlantilla = async (data) => {
        const id = data?.conversacion?.id || selIdRef.current;
        if (id) {
          try {
            const { data: full } = await api.get(`/whatsapp/conversaciones/${id}`);
            if (id === selIdRef.current) setSel(full);
          } catch {
            /* lo trae el refresco automatico */
          }
        }
        cargarLista({ silencioso: true });
      };
      const abrirNuevoChat = () => {
        setNuevoInicial(null);
        setNuevoOpen(true);
      };
      const abrirPorId = (id) => {
        if (!id) return;
        setParams({ c: id }, { state: { desdeLista: true } });
        cargarLista({ silencioso: true });
      };

  // PROMPT 98: abre "Qué sabe" con la pregunta del cliente ya escrita.
  const ensenar = (m) => {
    setPreguntaParaEnsenar((m?.texto || "").slice(0, 400));
    setConocimientoOpen(true);
  };
  // PROMPT 106: el turno humano (quien atiende lo que el agente deriva).
  const cargarTurno = async () => {
    try {
      const { data } = await api.get("/whatsapp/turno");
      setTurno(data);
    } catch (e) {
      /* si falla, el selector queda vacio y la pantalla sigue funcionando igual */
    }
  };

  const guardarTurno = async (usuarioId) => {
    try {
      const { data } = await api.put("/whatsapp/turno", {
        usuario_id: usuarioId || null,
        desde: turno?.desde || null,
        hasta: turno?.hasta || null,
      });
      setTurno(data);
      toast.success(data?.usuario ? `Ahora atiende ${data.usuario.nombre}.` : "Turno sin asignar.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const cargarPendientes = useCallback(async () => {
    try {
      const { data } = await api.get("/whatsapp/sin-respuesta");
      setPendientes(data?.items || []);
    } catch {
      setPendientes([]);
    }
  }, []);
  useEffect(() => { cargarPendientes(); }, [cargarPendientes]);
  useEffect(() => { cargarTurno(); }, []);  // PROMPT 106
  const cambiarEstado = async (estado) => {
    if (!sel) return;
    try {
      await api.post(`/whatsapp/conversaciones/${sel.id}/estado`, { estado });
      const { data: full } = await api.get(`/whatsapp/conversaciones/${sel.id}`);
      setSel(full);
      cargarLista();
      toast.success(
  estado === "agente"
    ? "El agente vuelve a atender esta conversación."
    : estado === "cerrada"
      ? "Conversación archivada. Vuelve sola a la lista si el cliente escribe."
      : `Conversación marcada: ${ESTADOS[estado]?.label || estado}.`
);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // ---- PROMPT 145: lo que llega EN VIVO (AvisosWhatsApp.jsx reparte los eventos "wa_*").
  const cargarListaRef = useRef(cargarLista);
  cargarListaRef.current = cargarLista;

  // PROMPT 149: etiquetas del equipo y cambios que vienen de la ficha del cliente.
  const cargarEtiquetas = useCallback(async () => {
    try {
      const { data } = await api.get("/whatsapp/etiquetas");
      setEtiquetas(data || []);
    } catch {
      /* sin etiquetas la pantalla se ve como antes */
    }
  }, []);
  useEffect(() => {
    cargarEtiquetas();
  }, [cargarEtiquetas]);
  useEffect(() => {
    if (etiquetaFiltro && !etiquetas.some((e) => e.id === etiquetaFiltro)) setEtiquetaFiltro("");
  }, [etiquetas, etiquetaFiltro]);
  const alActualizarConversacion = useCallback((id, cambios) => {
    setSel((actual) => (actual && actual.id === id ? { ...actual, ...cambios } : actual));
    setConvs((lista) =>
      lista.map((x) => {
        if (x.id !== id) return x;
        const propios = {};
        if (cambios.nombre_editado !== undefined) propios.nombre_editado = cambios.nombre_editado;
        if (cambios.etiquetas !== undefined) propios.etiquetas = cambios.etiquetas;
        if (cambios.bloqueado !== undefined) propios.bloqueado = cambios.bloqueado;
        if (cambios.sin_difusiones !== undefined) propios.sin_difusiones = cambios.sin_difusiones; // PROMPT 151
        return { ...x, ...propios };
      })
    );
  }, []);
  // PROMPT 150: bloquear y desbloquear (una sola vez para el chat y la ficha).
  const bloqueo = useBloqueo(sel, alActualizarConversacion);

  const alCambiarEtiquetas = useCallback(async () => {
    await cargarEtiquetas();
    cargarListaRef.current({ silencioso: true });
  }, [cargarEtiquetas]);

  // Una conversacion que cambio: sube o baja en la lista (respetando las fijadas), aparece si cumple
  // el filtro de ahora o se va si ya no lo cumple.
  const aplicarResumen = useCallback((c) => {
    const {
      estadoFiltro: mostrar,
      marcaFiltro: marca,
      etiquetaFiltro: etiqueta,
      q: busqueda,
      usuarioId: yo,
    } = mostrarRef.current;
    const previa = convsRef.current.find((x) => x.id === c.id);
    const encaja = coincideConLista(previa ? { ...previa, ...c } : c, mostrar, marca, yo, etiqueta);
    if (!previa) {
      // No esta en la lista de ahora: si cumple el filtro (o no se sabe) se vuelve a pedir la lista;
      // varios avisos seguidos hacen un solo pedido.
      if (!busqueda && encaja !== false) {
        clearTimeout(temporizadores.current.lista);
        temporizadores.current.lista = setTimeout(() => cargarListaRef.current({ silencioso: true }), 400);
      }
      return;
    }
    if (!busqueda && encaja === false) {
      setConvs((actual) => actual.filter((x) => x.id !== c.id));
      setTotalLista((n) => (n === null ? n : Math.max(0, n - 1)));
      return;
    }
    const fijadaAhora = Boolean(c.fijada) && !previa.fijada;
    setConvs((actual) =>
      ordenarLista(
        actual.map((x) =>
          x.id === c.id ? { ...x, ...c, ...(fijadaAhora ? { fijada_en: new Date().toISOString() } : {}) } : x
        )
      )
    );
  }, []);

  // La conversacion abierta trae lo nuevo (solo con la pestaña a la vista: pedir el detalle la marca leida).
  const traerAbiertaRef = useRef(null);
  traerAbiertaRef.current = async () => {
    const id = selIdRef.current;
    if (!id || document.visibilityState !== "visible") return;
    if (refrescandoConversacion.current) {
      // Hay un pedido en camino que pudo salir antes del mensaje: se repite enseguida.
      clearTimeout(temporizadores.current.detalle);
      temporizadores.current.detalle = setTimeout(() => traerAbiertaRef.current(), 400);
      return;
    }
    refrescandoConversacion.current = true;
    try {
      const { data } = await api.get(`/whatsapp/conversaciones/${id}`);
      if (selIdRef.current === id) {
        setSel((actual) => {
          if (actual?.id !== data?.id) return actual;
          const mezcla = mezclarDetalle(actual, data);
          return JSON.stringify(actual) !== JSON.stringify(mezcla) ? mezcla : actual;
        });
      }
    } catch {
      /* sin conexion: lo trae el refresco de respaldo */
    } finally {
      refrescandoConversacion.current = false;
    }
  };

  const alEventoWa = useCallback(
    (ev) => {
      if (ev.tipo === "wa_envio") {
        // Los tildes de un mensaje (enviado, entregado, leido).
        setSel((actual) =>
          actual &&
          actual.id === ev.conversacion_id &&
          (actual.mensajes || []).some((m) => m.id === ev.mensaje_id)
            ? {
                ...actual,
                mensajes: actual.mensajes.map((m) =>
                  m.id === ev.mensaje_id ? { ...m, estado_envio: ev.estado } : m
                ),
              }
            : actual
        );
        // PROMPT 148: la información del mensaje (horas de cada tilde, error explicado) viene con el
        // detalle: se pide de nuevo juntando los avisos seguidos.
        if (ev.conversacion_id === selIdRef.current) {
          clearTimeout(temporizadores.current.envio);
          temporizadores.current.envio = setTimeout(() => traerAbiertaRef.current(), 400);
        }
        return;
      }
      if (ev.tipo === "wa_escribiendo") {
        if (ev.conversacion_id !== selIdRef.current) return;
        if (ev.usuario_id === mostrarRef.current.usuarioId) return;
        setEscribiendo({ usuarioId: ev.usuario_id, nombre: ev.nombre || "Alguien" });
        clearTimeout(temporizadores.current.escribiendo);
        temporizadores.current.escribiendo = setTimeout(() => setEscribiendo(null), 6000);
        return;
      }
      if (ev.tipo !== "wa_mensaje" && ev.tipo !== "wa_conversacion") return;
      const c = ev.conversacion;
      if (!c?.id) return;
      aplicarResumen(c);
      if (c.id !== selIdRef.current) return;
      setSel((actual) => (actual && actual.id === c.id ? { ...actual, ...c } : actual));
      if (ev.tipo === "wa_mensaje" && ev.evento === "actualizado" && ev.mensaje && ev.mensaje.id) {
        // PROMPT 148: el cliente editó o borró un mensaje: se cambia ahí mismo, sin sonido ni burbuja nueva.
        const cambio = ev.mensaje;
        setSel((actual) =>
          actual && actual.id === c.id
            ? {
                ...actual,
                mensajes: (actual.mensajes || []).map((x) =>
                  x.id === cambio.id
                    ? {
                        ...x,
                        ...(cambio.editado ? { editado: true } : {}),
                        ...(cambio.eliminado ? { eliminado: true, texto: "" } : {}),
                      }
                    : x,
                ),
              }
            : actual,
        );
      }
      if (ev.tipo !== "wa_mensaje") return;
      // La abierta trae lo nuevo enseguida (un pedido del detalle con 300 ms de espera).
      clearTimeout(temporizadores.current.detalle);
      temporizadores.current.detalle = setTimeout(() => traerAbiertaRef.current(), 300);
      const entraDelCliente = c.canal === "whatsapp" && ev.mensaje?.direccion === "entrante" && ev.evento !== "actualizado";
      if (entraDelCliente && document.visibilityState === "visible") marcarLeida(c.id);
    },
    [aplicarResumen]
  );
  const suscribirAvisos = avisos.suscribir;
  useEffect(() => suscribirAvisos(alEventoWa), [suscribirAvisos, alEventoWa]);

  // Al volver a la pestaña con una conversacion abierta, trae lo que llego mientras no se veia.
  useEffect(() => {
    const alVolver = () => {
      if (document.visibilityState === "visible" && selIdRef.current) traerAbiertaRef.current();
    };
    document.addEventListener("visibilitychange", alVolver);
    return () => document.removeEventListener("visibilitychange", alVolver);
  }, []);

  useEffect(() => {
    setEscribiendo(null);
    setCita(null); // PROMPT 148
  }, [selId]);

  // PROMPT 149: la ficha del cliente se cierra al cambiar de conversacion.
  useEffect(() => {
    setFichaOpen(false);
  }, [selId]);

  useEffect(() => {
    const guardados = temporizadores.current;
    return () => Object.values(guardados).forEach((t) => clearTimeout(t));
  }, []);

  // Al llegar al final de la lista se pide la pagina siguiente (X-Siguiente).
  const cargarMas = useCallback(
    async (manual) => {
      if (!siguiente || cargandoMas) return;
      // Si la ultima pagina fallo, no se insiste sola: queda el boton "Cargar mas".
      if (manual !== true && Date.now() - ultimoErrorMas.current < 10000) return;
      const pedido = pedidoLista.current;
      setCargandoMas(true);
      try {
        const params = { ...paramsDeMostrar(estadoFiltro), limite: LIMITE_LISTA, antes: siguiente };
        if (marcaFiltro) params.marca_slug = marcaFiltro;
        if (etiquetaFiltro) params.etiqueta = etiquetaFiltro; // PROMPT 149
        if (q) params.q = q;
        const { data, headers } = await api.get("/whatsapp/conversaciones", { params });
        if (pedido !== pedidoLista.current) return; // mientras tanto cambio el filtro o la lista
        setConvs((actual) => {
          const ids = new Set(actual.map((c) => c.id));
          return [...actual, ...(data || []).filter((c) => !ids.has(c.id))];
        });
        setSiguiente(headers?.["x-siguiente"] || null);
      } catch (e) {
        ultimoErrorMas.current = Date.now();
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        setCargandoMas(false);
      }
    },
    [siguiente, cargandoMas, estadoFiltro, marcaFiltro, etiquetaFiltro, q]
  );
  useEffect(() => {
    const nodo = centinela.current;
    if (!siguiente || !nodo || typeof IntersectionObserver === "undefined") return undefined;
    const observador = new IntersectionObserver(
      (entradas) => {
        if (entradas.some((e) => e.isIntersecting)) cargarMas();
      },
      { root: contenedorLista.current, rootMargin: "0px 0px 240px 0px" }
    );
    observador.observe(nodo);
    return () => observador.disconnect();
  }, [siguiente, cargarMas, convs.length]);

  // Fijar, no leida, asignar y desarchivar.
  const alternarFija = async () => {
    if (!sel) return;
    const fijada = !sel.fijada;
    try {
      await api.post(`/whatsapp/conversaciones/${sel.id}/fijar`, { fijada });
      setSel((actual) => (actual && actual.id === sel.id ? { ...actual, fijada } : actual));
      aplicarResumen({ id: sel.id, fijada });
      toast.success(fijada ? "Conversación fijada arriba." : "Conversación desfijada.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail)); // incluye el tope de 10 fijadas
    }
  };

  // Como en WhatsApp: queda como no leida y se vuelve a la lista (si el chat siguiera abierto, pedir
  // el detalle la marcaria leida otra vez).
  const marcarNoLeida = async () => {
    if (!sel) return;
    try {
      clearTimeout(temporizadores.current.detalle);
      await api.post(`/whatsapp/conversaciones/${sel.id}/no-leida`);
      toast.success("Quedó como no leída.");
      irALista();
      cargarLista({ silencioso: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const asignarA = async (destino) => {
    if (!sel) return;
    const id = sel.id;
    try {
      const { data } = await api.post(`/whatsapp/conversaciones/${id}/asignar`, {
        usuario_id: destino || null,
      });
      const cambios = {
        id,
        asignada_a: data?.asignada_a || null,
        asignada_nombre: data?.asignada_nombre || null,
      };
      setSel((actual) => (actual && actual.id === id ? { ...actual, ...cambios } : actual));
      aplicarResumen(cambios);
      toast.success(
        cambios.asignada_a
          ? `Asignada a ${cambios.asignada_nombre || "esa persona"}.`
          : "La conversación quedó sin asignar."
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // La archivada vuelve a "Esperando a una persona" (la ruta de estado de siempre).
  const desarchivar = () => cambiarEstado("persona");

  // Las personas que se pueden elegir en "Asignada a" (las del turno) y la que ya la tiene.
  const equipoAsignable = (() => {
    const base = turno?.equipo || [];
    if (sel?.asignada_a && !base.some((u) => u.id === sel.asignada_a)) {
      return [...base, { id: sel.asignada_a, nombre: sel.asignada_nombre || "Otra persona" }];
    }
    return base;
  })();

  // Al escribir se avisa al resto del equipo (como mucho cada 4 segundos).
  const alEscribir = (valor) => {
    setTexto(valor);
    const id = selIdRef.current;
    if (!valor || !id || !esReal || modoEfectivo !== "persona") return;
    const ahora = Date.now();
    if (ahora - ultimoEscribiendo.current < 4000) return;
    ultimoEscribiendo.current = ahora;
    api.post(`/whatsapp/conversaciones/${id}/escribiendo`).catch(() => {});
  };
  // PROMPT 144: lo que se ve segun la conversacion abierta (o la de prueba nueva).
  const hayChat = Boolean(sel || enPrueba || idUrl);
  const enChatMovil = !vista.ancha && hayChat;
  const slugChat = sel?.marca_slug || (!sel && enPrueba ? marcaSlug : "") || "";
  const infoMarca = marcas.find((m) => m.slug === slugChat) || null;
  const colorChat = colorDeMarca(slugChat, marcas);
  const tonosChat = tonos(colorChat);
  const nombreRemitente = infoMarca?.remitente_nombre || infoMarca?.nombre || nombreCorto(slugChat, marcas) || "";
  const numeroMarca = infoMarca?.wa_display_number || "";
  const tituloChat = sel ? nombreVisible(sel) : enPrueba ? "Probar el agente" : "Cargando…";
  const lineaTelefono = sel?.telefono && tituloChat !== sel.telefono ? sel.telefono : "";
  const textoChip =
    esSimulador || !sel
      ? `${nombreRemitente || "Sin marca"} · prueba`
      : numeroMarca
        ? `${nombreRemitente} · ${numeroMarca}`
        : nombreRemitente;
  const quienResponde = `${nombreRemitente || "el equipo"}${numeroMarca ? ` (${numeroMarca})` : ""}`;
  const textoFranja = esReal
    ? `Respondés como ${quienResponde}`
    : modoEfectivo === "persona" && sel
      ? "Conversación de prueba: le respondés al cliente como persona del equipo"
      : "Conversación de prueba: escribís como el cliente";

  // Los avisos del boton Canal (los mismos de antes), para la barra de la computadora y el menu del celular.
  const badgesCanal = (
    <>
      {estadoAgente && !estadoAgente.canal_listo ? (
        <span className="ml-1 text-[11px] bg-slate-100 text-slate-600 rounded px-1.5">sin conectar</span>
      ) : null}
      {/* PROMPT 119: el número está conectado pero el agente no contesta solo. */}
      {estadoAgente?.agente_apagado ? (
        <span className="ml-1 text-[11px] bg-amber-100 text-amber-800 rounded px-1.5" data-testid="wa-agente-apagado">
          agente apagado
        </span>
      ) : null}
      {/* PROMPT 127: el agente contesta solo a los números de prueba. */}
      {estadoAgente?.agente_en_prueba ? (
        <span className="ml-1 text-[11px] bg-sky-100 text-sky-800 rounded px-1.5" data-testid="wa-agente-prueba">
          agente en prueba
        </span>
      ) : null}
    </>
  );

  // El menu "Agente" (en el celular va dentro del "⋯", junto con Canal y Actualizar).
  const itemsAgente = (
    <>
      <ItemMenu icono={GraduationCap} onClick={() => setPendientesOpen((v) => !v)} testid="wa-pendientes">
        <span>Lo que no supo</span>
        {pendientes.length ? <Contador n={pendientes.length} /> : null}
      </ItemMenu>
      <ItemMenu
        icono={BookOpen}
        onClick={() => {
          setPreguntaParaEnsenar("");
          setConocimientoOpen(true);
        }}
        testid="wa-conocimiento"
      >
        Qué sabe
      </ItemMenu>
      <ItemMenu icono={CreditCard} onClick={() => setPagosOpen(true)} testid="wa-cola-pagos">
        <span>Cola de pagos</span>
        {estadoAgente?.pagos_pendientes ? <Contador n={estadoAgente.pagos_pendientes} /> : null}
      </ItemMenu>
      {/* PROMPT 106: quien atiende lo que el agente deriva. Se cambia desde aca. */}
      <div className="flex items-center gap-2 px-2.5 py-1.5">
        <User className="h-4 w-4 shrink-0 text-slate-500" />
        <select
          value={turno?.usuario_id || ""}
          onChange={(e) => guardarTurno(e.target.value)}
          data-testid="wa-turno"
          title="Quién atiende las conversaciones que el agente deriva"
          className="h-9 min-w-0 flex-1 rounded-md border border-slate-200 bg-white px-2 text-base text-slate-700 lg:text-sm"
        >
          <option value="">De turno: nadie</option>
          {(turno?.equipo || []).map((u) => (
            <option key={u.id} value={u.id}>
              De turno: {u.nombre}
            </option>
          ))}
        </select>
      </div>
      {avisos.activo && (
        <ItemMenu icono={Bell} onClick={() => setAvisosOpen(true)} testid="wa-avisos">
          Avisos en esta computadora
        </ItemMenu>
      )}
      {/* PROMPT 152: tambien con el navegador sin avisos de escritorio (iPhone): ahi esta la guia */}
      <ItemMenu icono={Smartphone} onClick={() => setAvisosPushOpen(true)} testid="wa-avisos-push">
        Avisos en este dispositivo
      </ItemMenu>
      <ItemMenu icono={IconoRapidas} onClick={() => setRapidasOpen(true)} testid="wa-menu-rapidas">
          Respuestas rápidas
        </ItemMenu>
        <ItemMenu icono={IconoEtiqueta} onClick={() => setEtiquetasOpen(true)} testid="wa-menu-etiquetas">
          Etiquetas
        </ItemMenu>
        <ItemMenu icono={Bot} onClick={nueva} testid="wa-nueva-conversacion">
        Probar el agente
      </ItemMenu>
    </>
  );

  // La franja de la zona de escribir, en el color de la marca (va entre el hilo y el cuadro de texto).
  const franja = (
    <div
      className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1.5 border-l-4 px-3 py-2 text-[13px]"
      style={{ borderLeftColor: tonosChat.fuerte, backgroundColor: tonosChat.suave, color: tonosChat.texto }}
      data-testid="wa-franja"
    >
      <span className="min-w-0 font-medium">{textoFranja}</span>
      {/* PROMPT 95 y 144: los dos modos de escribir solo existen en las conversaciones de prueba. */}
      {esSimulador && (
        <span className="flex flex-wrap items-center gap-2" data-testid="wa-modo">
          <Button
            variant={modo === "persona" ? "default" : "outline"}
            size="sm"
            onClick={() => setModo("persona")}
            data-testid="wa-modo-persona"
            className="h-8 gap-1.5"
          >
            <UserCheck className="h-3.5 w-3.5" /> Responderle al cliente
          </Button>
          <Button
            variant={modo === "cliente" ? "default" : "outline"}
            size="sm"
            onClick={() => setModo("cliente")}
            data-testid="wa-modo-cliente"
            className="h-8 gap-1.5"
          >
            <User className="h-3.5 w-3.5" /> Escribir como el cliente
          </Button>
        </span>
      )}
      {esReal && sel && (
        <span
          className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-xs font-medium ${
            sel.ventana_abierta
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-amber-50 text-amber-700 border-amber-200"
          }`}
          data-testid="wa-ventana"
        >
          <Clock className="h-3 w-3" />
          {sel.ventana_abierta
            ? `Quedan ${Math.floor((sel.minutos_de_ventana || 0) / 60)} h para responder`
            : "Pasaron las 24 horas: solo plantilla"}
        </span>
      )}
    </div>
  );

  return (
    <div
      className={enChatMovil ? "fixed inset-x-0 z-40 flex flex-col bg-white" : "flex h-full min-h-0 flex-col"}
      style={enChatMovil ? { top: vista.arriba, height: vista.alto } : undefined}
      data-testid="agente-whatsapp-page"
    >
      {/* PROMPT 144: barra de arriba compacta; en el celular, con un chat abierto, el chat ocupa todo. */}
      {!enChatMovil && (
        <div
          className="flex shrink-0 items-center justify-between gap-2 px-3 py-2 lg:px-1 lg:pb-2 lg:pt-0"
          data-testid="wa-barra"
        >
          <div className="min-w-0 flex-1">
{estadoAgente && (
  <p className="text-xs leading-snug text-slate-500" data-testid="agente-estado">
    {/* PROMPT 127: no alcanza con que la clave este cargada: si la ultima llamada fallo, se dice por que. */}
    {estadoAgente.ia?.ok === false ? (
      <span className="text-red-600" title={estadoAgente.ia.mensaje || undefined} data-testid="agente-ia-falla">
        IA sin respuesta{estadoAgente.ia.motivo === "sin_saldo" ? " (sin saldo)" : ""}
      </span>
    ) : (
      <>IA {estadoAgente.llm_disponible ? "conectada" : "no disponible"}</>
    )}{" "}
    · {estadoAgente.conversaciones} conversación(es) ·{" "}
    {estadoAgente.con_persona} esperando a una persona{typeof estadoAgente.pagos_pendientes === "number" ? ` · ${estadoAgente.pagos_pendientes} esperando cobro` : ""}{estadoAgente.conversaciones_whatsapp ? ` · ${estadoAgente.conversaciones_whatsapp} por WhatsApp` : ""}
  </p>
)}
            {!estadoAgente && <p className="text-xs text-slate-500">Conversaciones del agente</p>}
          </div>
          {vista.ancha ? (
            <div className="flex shrink-0 items-center gap-2">
              <MenuChico
                testid="wa-menu-agente"
                titulo="Lo que no supo, qué sabe, cola de pagos, de turno y probar el agente"
                ancho="w-72"
                boton={
                  <>
                    Agente
                    {pendientes.length ? <Contador n={pendientes.length} /> : null}
                    <ChevronDown className="h-4 w-4" />
                  </>
                }
              >
                {itemsAgente}
              </MenuChico>
              <Button variant="outline" size="sm" onClick={abrirNuevoChat} data-testid="wa-nuevo-chat" className="gap-2">
                <MessageCircle className="h-4 w-4" /> Nuevo chat
              </Button>
              <Button variant="outline" size="sm" onClick={() => setCanalOpen(true)} data-testid="wa-canal" className="gap-2">
                <Radio className="h-4 w-4" /> Canal
                {badgesCanal}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPlantillasOpen(true)}
                data-testid="wa-plantillas"
                className="gap-2"
              >
                <FileText className="h-4 w-4" /> Plantillas
              </Button>
              {isAdmin ? (
                <Button variant="outline" size="sm" onClick={() => setDifusionesOpen(true)} data-testid="wa-difusiones" className="gap-2">
                  <Megaphone className="h-4 w-4" /> Difusiones
                </Button>
              ) : null}
              <Button variant="outline" size="sm" onClick={cargarLista} data-testid="wa-refrescar" className="gap-2">
                <RefreshCw className="h-4 w-4" /> Actualizar
              </Button>
            </div>
          ) : (
            <MenuChico
              testid="wa-menu-mas"
              titulo="Más opciones"
              ancho="w-72"
              boton={<MoreHorizontal className="h-4 w-4" />}
            >
              {itemsAgente}
              <ItemMenu icono={MessageCircle} onClick={abrirNuevoChat} testid="wa-nuevo-chat">
                Nuevo chat
              </ItemMenu>
              <ItemMenu icono={Radio} onClick={() => setCanalOpen(true)} testid="wa-canal">
                <span>Canal</span>
                {badgesCanal}
              </ItemMenu>
              <ItemMenu icono={FileText} onClick={() => setPlantillasOpen(true)} testid="wa-plantillas">
                Plantillas
              </ItemMenu>
              {isAdmin ? (
                <ItemMenu icono={Megaphone} onClick={() => setDifusionesOpen(true)} testid="wa-difusiones">
                  Difusiones
                </ItemMenu>
              ) : null}
              <ItemMenu icono={RefreshCw} onClick={cargarLista} testid="wa-refrescar">
                Actualizar
              </ItemMenu>
            </MenuChico>
          )}
        </div>
      )}

<ColaPagosDialog open={pagosOpen} onOpenChange={setPagosOpen} onCambio={cargarLista} />
<CanalWhatsAppDialog open={canalOpen} onOpenChange={setCanalOpen} onCambio={cargarLista} />
<PlantillasDialog open={plantillasOpen} onOpenChange={setPlantillasOpen} />
{isAdmin ? <DifusionesDialog open={difusionesOpen} onOpenChange={setDifusionesOpen} marcas={marcas} /> : null}
<RespuestasRapidasDialog open={rapidasOpen} onOpenChange={setRapidasOpen} marcas={marcas} />
<EtiquetasDialog
  open={etiquetasOpen}
  onOpenChange={setEtiquetasOpen}
  etiquetas={etiquetas}
  onCambio={alCambiarEtiquetas}
/>
<ElegirPlantillaDialog open={elegirOpen} onOpenChange={setElegirOpen} conversacion={sel} onCambio={alElegirPlantilla} />
<NuevoChatDialog open={nuevoOpen} onOpenChange={setNuevoOpen} marcas={marcas} inicial={nuevoInicial} onAbrir={abrirPorId} />
<AvisosEscritorioDialog open={avisosOpen} onOpenChange={setAvisosOpen} />
<AvisosPushDialog open={avisosPushOpen} onOpenChange={setAvisosPushOpen} marcas={marcas} />
<ConocimientoDialog
  open={conocimientoOpen}
  onOpenChange={(v) => { setConocimientoOpen(v); if (!v) { setPreguntaParaEnsenar(""); cargarPendientes(); } }}
  marcas={marcas}
  preguntaInicial={preguntaParaEnsenar}
/>

{/* PROMPT 98: la lista de trabajo — lo que el agente no supo contestar. */}
{!enChatMovil && pendientesOpen && (
  <Card className="mx-3 mb-2 shrink-0 p-3 lg:mx-1" data-testid="wa-pendientes-panel">
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm font-medium text-slate-900">Lo que no supo contestar</span>
      <Button variant="ghost" size="sm" onClick={cargarPendientes} className="gap-1.5">
        <RefreshCw className="h-3.5 w-3.5" /> Actualizar
      </Button>
    </div>
    {pendientes.length === 0 ? (
      <p className="text-sm text-slate-400 py-4 text-center">
        Nada pendiente. Cuando el agente derive algo, aparece acá.
      </p>
    ) : (
      <div className="space-y-1.5 max-h-[40vh] overflow-y-auto">
        {pendientes.map((p) => (
          <div key={p.mensaje_id} className="flex items-start justify-between gap-3 border-b border-slate-100 pb-1.5" data-testid={`wa-pendiente-${p.mensaje_id}`}>
            <div className="min-w-0">
              <p className="text-sm text-slate-800">{p.pregunta}</p>
              <p className="text-[11px] text-slate-400">{p.motivo || "derivó"}{p.marca_slug ? ` · ${nombreMarca(p.marca_slug)}` : ""}</p>
            </div>
            <div className="flex gap-1 shrink-0">
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setPreguntaParaEnsenar(p.pregunta); setConocimientoOpen(true); }}
                data-testid={`wa-ensenar-pendiente-${p.mensaje_id}`}
                className="gap-1.5"
              >
                <GraduationCap className="h-3.5 w-3.5" /> Enseñarle
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={async () => {
                  try {
                    await api.post(`/whatsapp/sin-respuesta/${p.mensaje_id}/listo`);
                    cargarPendientes();
                  } catch (e) {
                    toast.error(formatApiError(e.response?.data?.detail));
                  }
                }}
                data-testid={`wa-descartar-${p.mensaje_id}`}
              >
                Descartar
              </Button>
            </div>
          </div>
        ))}
      </div>
    )}
  </Card>
)}

      <div
        className="flex min-h-0 flex-1 overflow-hidden bg-white lg:rounded-lg lg:border lg:border-slate-200 lg:shadow-sm"
        data-testid="wa-paneles"
      >
        {/* Lista */}
        <aside
          className={`${
            hayChat ? "hidden lg:flex" : "flex"
          } min-h-0 w-full min-w-0 flex-col bg-white lg:w-[380px] lg:shrink-0 lg:border-r lg:border-slate-200 2xl:w-[420px]`}
          data-testid="wa-lista"
        >
          <div className="shrink-0 space-y-2 border-b border-slate-100 p-3">
            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="relative min-w-0 flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Teléfono, nombre o texto…"
                  className="pl-9"
                  data-testid="wa-buscar"
                />
              </div>
              <Select value={estadoFiltro} onValueChange={setEstadoFiltro}>
                <SelectTrigger data-testid="wa-filtro-estado" className="w-full sm:w-[13rem]">
                  <span className="flex min-w-0 items-center gap-1 truncate">
                    <span className="text-slate-500">Mostrar:</span>
                    <SelectValue />
                  </span>
                </SelectTrigger>
                <SelectContent>
                  {MOSTRAR.map((o) => (
                    <SelectItem key={o.valor} value={o.valor}>
                      {o.etiqueta}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
        {/* PROMPT 149: filtro por etiqueta (solo si hay etiquetas). */}
        {etiquetas.length > 0 ? (
          <div className="flex items-center gap-2" data-testid="wa-filtro-etiqueta-caja">
            <Select value={etiquetaFiltro || "todas"} onValueChange={(v) => setEtiquetaFiltro(v === "todas" ? "" : v)}>
              <SelectTrigger data-testid="wa-filtro-etiqueta" className="w-full sm:w-[13rem]">
                <span className="flex min-w-0 items-center gap-1 truncate">
                  <span className="text-slate-500">Etiqueta:</span>
                  <SelectValue />
                </span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                {etiquetas.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
            {/* PROMPT 144: filtro por marca (se desplaza de costado en el celular). */}
            {marcas.length > 0 && (
              <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-0.5" data-testid="wa-filtro-marca">
                <button
                  type="button"
                  onClick={() => setMarcaFiltro("")}
                  aria-pressed={marcaFiltro === ""}
                  data-testid="wa-filtro-marca-todas"
                  className={`shrink-0 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium ${
                    marcaFiltro === ""
                      ? "border-slate-800 bg-slate-800 text-white"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  Todas
                </button>
                {marcas.map((m) => {
                  const color = colorDeMarca(m.slug, marcas);
                  const activa = marcaFiltro === m.slug;
                  return (
                    <button
                      key={m.slug}
                      type="button"
                      onClick={() => setMarcaFiltro(activa ? "" : m.slug)}
                      aria-pressed={activa}
                      data-testid={`wa-filtro-marca-${m.slug}`}
                      className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                      style={activa ? { borderColor: color, backgroundColor: tonos(color).suave } : undefined}
                    >
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
                      {nombreCorto(m.slug, marcas)}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div
            ref={contenedorLista}
            className="min-h-0 flex-1 divide-y divide-slate-100 overflow-y-auto"
            data-testid="wa-lista-conversaciones"
          >
            {totalLista !== null && !loading && (
              <p className="px-4 py-1.5 text-xs text-slate-500" data-testid="wa-total">
                {totalLista === 1 ? "1 conversación" : `${totalLista} conversaciones`}
              </p>
            )}
            {loading ? (
              <div className="flex justify-center py-10">
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              </div>
            ) : convsVisibles.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-slate-400">
                {marcaFiltro
                  ? "No hay conversaciones de esta marca."
                  : q
                  ? "No encontramos conversaciones con esa búsqueda."
                  : estadoFiltro !== "todas"
                  ? "No hay conversaciones con este filtro."
                  : "Todavía no hay conversaciones. Probá el agente desde el menú Agente."}
              </p>
            ) : (
              convsVisibles.map((c) => {
                // PROMPT 123: como la lista de chats de WhatsApp: iniciales, ultimo mensaje
                // ("📷 Foto", "🎤 Audio"...), hora y los no leidos en verde.
                const noLeidos = sel?.id !== c.id ? c.no_leidos || 0 : 0;
                // PROMPT 144: cada marca con su color (barra de la izquierda, anillo del avatar y chip).
                const color = colorDeMarca(c.marca_slug, marcas);
                const t = tonos(color);
                return (
                  <button
                    key={c.id}
                    onClick={() => abrir(c)}
                    style={{ borderLeftColor: color }}
                    className={`flex w-full items-center gap-3 border-l-4 px-3 py-3 text-left hover:bg-slate-50 ${
                      sel?.id === c.id ? "bg-slate-100" : ""
                    }`}
                    data-testid={`wa-conv-${c.id}`}
                  >
                    <Avatar nombre={nombreParaAvatar(c)} telefono={c.telefono} className="h-12 w-12 text-base" anillo={color} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="flex min-w-0 items-center gap-1">
                          {c.fijada && (
                            <Pin
                              className="h-3.5 w-3.5 shrink-0 text-slate-400"
                              data-testid={`wa-fijada-${c.id}`}
                              aria-label="Fijada"
                            />
                          )}
                          <span className="truncate text-[15px] font-medium text-slate-900">{nombreVisible(c)}</span>
                        </span>
                        <span
                          className={`shrink-0 text-xs ${noLeidos ? "font-semibold text-emerald-600" : "text-slate-400"}`}
                        >
                          {horaLista(c.ultimo_mensaje_en)}
                        </span>
                      </div>
                      <div className="mt-0.5 flex items-center justify-between gap-2">
                        <span className={`truncate text-sm ${noLeidos ? "text-slate-800" : "text-slate-500"}`}>
                          {c.ultimo_mensaje || "—"}
                        </span>
                        {noLeidos ? (
                          <span
                            className="shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-500 text-white text-[11px] font-semibold flex items-center justify-center"
                            data-testid={`wa-no-leidos-${c.id}`}
                          >
                            {noLeidos}
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-1 flex min-w-0 items-center gap-1.5 text-xs">
                        {c.canal === "simulador" && (
                          <span className="shrink-0 whitespace-nowrap rounded bg-slate-200 px-1.5 py-0.5 text-[11px] font-medium text-slate-700">
                            Prueba
                          </span>
                        )}
                        {c.marca_slug && (
                          <span
                            className="shrink-0 whitespace-nowrap rounded border px-1.5 py-0.5 text-xs font-medium"
                            style={{ backgroundColor: t.suave, color: t.texto, borderColor: t.borde }}
                          >
                            {nombreCorto(c.marca_slug, marcas)}
                          </span>
                        )}
                        <span className="min-w-0 truncate text-slate-500">
                          {c.telefono || SIN_NUMERO}
                          {c.idioma ? ` · ${c.idioma.toUpperCase()}` : ""}
                        </span>
                        <span className="shrink-0 whitespace-nowrap">
                          <EstadoBadge estado={c.estado} celular={c.atiende_celular} sinAgente={sinAgente(c)} corto />
                        </span>
                        {c.asignada_a && (
                          <span
                            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-semibold text-slate-700"
                            title={`Asignada a ${c.asignada_nombre || "otra persona"}`}
                            data-testid={`wa-asignada-${c.id}`}
                          >
                            {iniciales(c.asignada_nombre)}
                          </span>
                        )}
                      </div>
                  <EtiquetasEnFila ids={c.etiquetas} etiquetas={etiquetas} /> {/* PROMPT 149 */}
                    </div>
                  </button>
                );
              })
            )}
            {siguiente && (
              <div ref={centinela} className="flex justify-center p-3" data-testid="wa-centinela">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => cargarMas(true)}
                  disabled={cargandoMas}
                  data-testid="wa-cargar-mas"
                  className="gap-2 bg-white"
                >
                  {cargandoMas ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Cargar más conversaciones
                </Button>
              </div>
            )}
            </div>
            </aside>

        {/* Hilo + simulador */}
        <section
          className={`${hayChat ? "flex" : "hidden lg:flex"} min-h-0 min-w-0 flex-1 flex-col`}
          data-testid="wa-chat"
        >
          {hayChat ? (
            <>
              {/* PROMPT 144: encabezado con el color de la marca; el chip dice por que numero escribio el cliente. */}
              <div
                className="shrink-0 border-b border-t-4 border-slate-200 px-3 py-2.5"
                style={{ borderTopColor: tonosChat.fuerte, backgroundColor: tonosChat.suave }}
                data-testid="wa-encabezado"
              >
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <div className="flex min-w-0 flex-1 basis-60 items-start gap-2.5">
                    {!vista.ancha && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={irALista}
                        aria-label="Volver a la lista"
                        data-testid="wa-volver"
                        className="-ml-1 h-10 w-10 shrink-0"
                      >
                        <ArrowLeft className="h-5 w-5" />
                      </Button>
                    )}
                    {sel ? (
                      <Avatar
                        nombre={nombreParaAvatar(sel)}
                        telefono={sel.telefono}
                        className="h-10 w-10 text-sm"
                        anillo={colorChat}
                      />
                    ) : (
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-slate-500">
                        <Bot className="h-5 w-5" />
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="min-w-0 truncate text-base font-semibold leading-tight text-slate-900">
                          {tituloChat}
                        </span>
                        {sel && <EstadoBadge estado={sel.estado} celular={sel.atiende_celular} sinAgente={sinAgente(sel)} />}
                      {sel && sel.bloqueado ? <ChipBloqueado /> : null}
                      </div>
                      {lineaTelefono && <div className="mt-0.5 truncate text-xs text-slate-600">{lineaTelefono}</div>}
                      {slugChat && (
                        <span
                          className="mt-1.5 inline-block max-w-full rounded-lg px-3 py-1 text-[13px] font-semibold"
                          style={{ backgroundColor: tonosChat.fuerte, color: tonosChat.sobreFuerte }}
                          data-testid="wa-marca-badge"
                        >
                          {textoChip}
                        </span>
                      )}
                    </div>
                  </div>
                  {sel && (
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    {/* PROMPT 145: a quien le toca esta conversacion. */}
                {/* PROMPT 149: etiquetas y ficha del cliente. */}
                {esReal && (
                  <>
                    <EtiquetasDeLaConversacion
                      conversacion={sel}
                      etiquetas={etiquetas}
                      onCambio={(id, ids) => alActualizarConversacion(id, { etiquetas: ids })}
                      onCrear={(nueva) => setEtiquetas((lista) => [...lista, nueva])}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setFichaOpen((v) => !v)}
                      aria-pressed={fichaOpen}
                      data-testid="wa-info-contacto"
                      className="gap-1.5 bg-white"
                    >
                      <IconoInfo className="h-3.5 w-3.5" /> Info
                    </Button>
                  </>
                )}
                    {esReal && (
                      <div className="flex items-center gap-1.5" data-testid="wa-asignacion">
                        <label htmlFor="wa-asignada" className="hidden text-xs text-slate-500 xl:inline">
                          Asignada a
                        </label>
                        <select
                          id="wa-asignada"
                          value={sel.asignada_a || ""}
                          onChange={(e) => asignarA(e.target.value)}
                          data-testid="wa-asignada"
                          title="Quién se encarga de esta conversación"
                          aria-label="Asignada a"
                          className="h-8 max-w-[11rem] rounded-md border border-slate-200 bg-white px-2 text-base text-slate-700 lg:text-sm"
                        >
                          <option value="">Sin asignar</option>
                          {equipoAsignable.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.nombre}
                            </option>
                          ))}
                        </select>
                        {usuarioId && sel.asignada_a !== usuarioId && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => asignarA(usuarioId)}
                            data-testid="wa-tomar"
                            className="gap-1.5 bg-white"
                          >
                            <UserCheck className="h-3.5 w-3.5" /> Tomar
                          </Button>
                        )}
                      </div>
                    )}
                      {sel.estado !== "agente" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => cambiarEstado("agente")}
                          data-testid="wa-devolver-agente"
                          className="gap-1.5 bg-white"
                        >
                          <Bot className="h-3.5 w-3.5" /> Devolver al agente
                        </Button>
                      )}
                      {sel.estado === "agente" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => cambiarEstado("persona")}
                          data-testid="wa-tomar-persona"
                          className="gap-1.5 bg-white"
                        >
                          <UserCheck className="h-3.5 w-3.5" /> La toma una persona
                        </Button>
                      )}
                      {sel.estado !== "cerrada" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => cambiarEstado("cerrada")}
                          data-testid="wa-cerrar"
                          title="Vuelve sola a la lista si el cliente escribe"
                        >
                          Archivar
                        </Button>
                      )}
                      <MenuChico
                        testid="wa-menu-chat"
                        titulo="Más acciones de esta conversación"
                        ancho="w-64"
                        boton={<MoreHorizontal className="h-4 w-4" />}
                      >
                        {esReal && (
                          <ItemMenu
                            icono={sel.fijada ? PinOff : Pin}
                            onClick={alternarFija}
                            testid="wa-fijar"
                          >
                            {sel.fijada ? "Desfijar" : "Fijar arriba"}
                          </ItemMenu>
                        )}
                        {esReal && (
                          <ItemMenu icono={EyeOff} onClick={marcarNoLeida} testid="wa-no-leida">
                            Marcar como no leída
                          </ItemMenu>
                        )}
                        {esReal && (sel.telefono || sel.bloqueado) && (
                          <ItemMenu
                            icono={Ban}
                            onClick={sel.bloqueado ? bloqueo.desbloquear : bloqueo.pedir}
                            testid="wa-bloquear"
                          >
                            {sel.bloqueado ? "Desbloquear cliente" : "Bloquear cliente"}
                          </ItemMenu>
                        )}
                        {sel.estado === "cerrada" ? (
                          <ItemMenu icono={ArchiveRestore} onClick={desarchivar} testid="wa-menu-desarchivar">
                            Desarchivar
                          </ItemMenu>
                        ) : (
                          <ItemMenu
                            icono={Archive}
                            onClick={() => cambiarEstado("cerrada")}
                            testid="wa-menu-archivar"
                          >
                            Archivar
                          </ItemMenu>
                        )}
                        <p className="px-2.5 pb-1.5 pt-1 text-xs text-slate-500">
                          Una conversación archivada vuelve sola a la lista si el cliente escribe.
                        </p>
                      </MenuChico>
                    </div>
                  )}
                </div>
              </div>

{escribiendo && sel && (
  <div
    className="shrink-0 border-b border-slate-100 bg-white px-3 py-1 text-xs text-slate-500"
    data-testid="wa-escribiendo"
    aria-live="polite"
  >
    {escribiendo.nombre} está escribiendo…
  </div>
)}
{!sel && enPrueba && (
  <div className="shrink-0 p-3 border-b border-slate-100 grid grid-cols-1 sm:grid-cols-3 gap-3 bg-slate-50/60">
    <div className="space-y-1.5">
      <Label className="text-xs">Marca por la que escribe</Label>
      <Select value={marcaSlug} onValueChange={setMarcaSlug}>
        <SelectTrigger data-testid="wa-marca"><SelectValue placeholder="Elegí la marca" /></SelectTrigger>
        <SelectContent>
          {marcas.map((m) => (
            <SelectItem key={m.slug} value={m.slug}>{m.remitente_nombre || m.nombre}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
    <div className="space-y-1.5">
      <Label className="text-xs">Teléfono del cliente (simulado)</Label>
      <Input value={telefono} onChange={(e) => setTelefono(e.target.value)} data-testid="wa-telefono" />
    </div>
    <div className="space-y-1.5">
      <Label className="text-xs">Nombre (opcional)</Label>
      <Input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Cliente de prueba" data-testid="wa-nombre" />
    </div>
  </div>
)}

              {/* PROMPT 123: el hilo y el cuadro de escribir, como WhatsApp (fotos, audios,
                  documentos y notas de voz). Ver components/agente/ChatWhatsApp.jsx. */}
              <ChatWhatsApp
                conversacion={sel}
                onEnsenar={ensenar}
                onCargarAnteriores={cargarAnteriores}
                onAbrirConversacion={abrir}
                vacio={
                  <p className="mx-auto mt-10 max-w-md rounded-lg bg-white/85 px-4 py-3 text-center text-sm text-slate-600 shadow-sm">
                    Escribí abajo como lo haría un cliente. Por ejemplo: "Hola, quiero ir a Washington el sábado, somos 4".
                  </p>
                }
                modoPersona={modoEfectivo === "persona" && !!sel}
                ventanaCerrada={ventanaCerrada}
                texto={texto}
                setTexto={alEscribir}
                enviando={enviando}
                onEnviarTexto={enviar}
                etiquetaEnviar={modoEfectivo === "persona" && sel ? "Responder" : "Enviar"}
                placeholder={
                  modoEfectivo === "persona" && sel
                    ? ventanaCerrada
                      ? "Pasaron 24 horas desde el último mensaje del cliente: hace falta una plantilla."
                      : "Tu respuesta al cliente…"
                    : sel?.estado === "persona"
                      ? "La conversación la sigue una persona: el agente no va a responder."
                      : "Mensaje del cliente…"
                }
                onArchivoEnviado={alEnviarArchivo}
                bloqueo={bloqueo}
                acciones={{ cita, setCita, conversaciones: convs, marcas }} // PROMPT 148
                barra={franja}
                onDesarchivar={sel?.estado === "cerrada" ? desarchivar : undefined}
                onPlantilla={esReal && sel ? () => setElegirOpen(true) : undefined}
              />
            </>
          ) : (
            <div
              className="m-auto flex max-w-xs flex-col items-center gap-2 px-6 text-center text-slate-400"
              data-testid="wa-sin-seleccion"
            >
              <MessageCircle className="h-10 w-10" />
              <p className="text-sm">Elegí una conversación de la lista para ver el chat y responder.</p>
            </div>
          )}
        </section>
        {/* PROMPT 149: ficha del cliente (a la derecha en la computadora, pantalla completa en el celular). */}
        {bloqueo.dialogo}
        {fichaOpen && sel && esReal ? (
          <FichaCliente
            conversacion={sel}
            marcaTexto={textoChip}
            marcaColor={colorChat}
            etiquetas={etiquetas}
            onCerrar={() => setFichaOpen(false)}
            onActualizar={alActualizarConversacion}
            bloqueo={bloqueo}
            onAbrirConversacion={abrir}
          />
        ) : null}
      </div>
    </div>
  );
}
